import { capitalize } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { DropdownButton, Table } from 'react-bootstrap';
import { getOrgTermsContent } from '../../../content/utils.js';
import AuthorizedAction, {
  isAuthorized,
  isUnAuthorized,
} from '../../AuthorizedAction.js';
import { NoRoleIcon } from '../../icons/NoRoleIcon.jsx';
import CustomTooltip from '../../CustomTooltip.jsx';
import { PlusButtonBlueIcon } from '../../icons/PlusButtonBlueIcon.jsx';
import { AddRoleLink, UserLink } from '../../links/index.jsx';
import UserActionsDropdown from './UserActionsDropdown.jsx';
import { EyeIcon } from '../../icons/EyeIcon.jsx';
import styles from './UsersTable.module.css';
import CustomMenuItem from '../../CustomMenuItem.jsx';

const UsersTableRow = (
  slug,
  user,
  currentUserId,
  onDelete,
  onSignOut,
  permissions,
  restrictions,
  currentUserRole
) => {
  const {
    id,
    fullName,
    employeeId,
    status,
    orgRole,
    lastActive,
    projectsCount,
    initiativesCount,
    changeImpactsCount,
    avatar,
  } = user;

  const checkAuthorize = isAuthorized('update', 'user', permissions);
  const isBlockedDeletedUser = isUnAuthorized(
    'destroy',
    'userOrganizationRole',
    restrictions
  );

  const renderAddRole = () => {
    return id !== currentUserId ? (
      <div className="actions">
        <DropdownButton
          noCaret
          title=""
          id="id"
          className="btn-more btn btn-default"
        >
          <UserLink userId={id} slug={slug}>
            <CustomMenuItem>
              <span className="flex">
                <EyeIcon />
                <span>&nbsp; View</span>
              </span>
            </CustomMenuItem>
            <CustomMenuItem divider />
          </UserLink>
          <AddRoleLink userId={id} slug={slug}>
            <CustomMenuItem>
              <span className="flex">
                <PlusButtonBlueIcon />
                <span>&nbsp;Add New Role</span>
              </span>
            </CustomMenuItem>
            <CustomMenuItem divider />
          </AddRoleLink>
        </DropdownButton>
      </div>
    ) : null;
  };

  return (
    <tr key={id}>
      <td>
        <UserLink userId={id}>
          <span className="flex flex-1 strong text-active cursor-pointer">
            {status === 'no_roles' ? (
              <CustomTooltip
                tooltip={
                  <div style={{ maxWidth: '300px' }}>
                    <strong>
                      <h5
                        style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          lineHeight: '20px',
                          color: '#153142',
                        }}
                        className="m-b-8"
                      >
                        This User doesn’t have any Role in your Organization,{' '}
                        {getOrgTermsContent('project')} or Group.
                      </h5>
                    </strong>
                    <p
                      style={{
                        fontSize: '12px',
                        fontWeight: '400',
                        lineHeight: '20px',
                        color: '#7F8FA4',
                      }}
                      className="m-b-0 text-muted"
                    >
                      An invitation to join ChangeSync will be sent after at
                      least one role is assigned on the Permissions tab.
                    </p>
                  </div>
                }
                id="tooltip"
              >
                <div className="profile-avatar medium m-r-10">
                  <img src={avatar} alt="avatar" />
                  <div className="no-roles">
                    <NoRoleIcon />
                  </div>
                </div>
              </CustomTooltip>
            ) : (
              <div className="profile-avatar medium m-r-10">
                <img src={avatar} alt="avatar" />
              </div>
            )}
            <UserLink userId={id}>{fullName}</UserLink>
          </span>
        </UserLink>
      </td>
      <td>
        {status === 'no_roles' ? (
          '—'
        ) : (
          <span className={`label label-status-${status}`}>
            {capitalize(status)}
          </span>
        )}
      </td>
      <td>
        {!orgRole ? '—' : <span className="label">{capitalize(orgRole)}</span>}
      </td>
      <td>
        <strong>{!lastActive ? '—' : lastActive}</strong>
      </td>
      <td>
        <strong>{Number(projectsCount)}</strong>
      </td>
      <td>
        <strong>{Number(initiativesCount)}</strong>
      </td>
      <td>
        <strong>{Number(changeImpactsCount)}</strong>
      </td>
      <td>
        <div className="flex flex-end">
          {checkAuthorize ? (
            <AuthorizedAction action="update" subject="user">
              <UserActionsDropdown
                slug={slug}
                userId={id}
                currentUserId={currentUserId}
                onDelete={onDelete}
                onSignOut={onSignOut}
                isBlockedDeletedUser={isBlockedDeletedUser}
                currentUserRole={currentUserRole}
                employeeId={employeeId}
                orgRole={orgRole}
              />
            </AuthorizedAction>
          ) : (
            renderAddRole()
          )}
        </div>
      </td>
    </tr>
  );
};

const UsersTable = ({
  slug,
  users,
  currentUserId,
  onDelete,
  onSignOut,
  permissions,
  restrictions,
  currentUserRole,
}) => (
  <Table hover className={styles.usersTable}>
    <thead>
      <tr>
        <th>User</th>
        <th>Status</th>
        <th>Organization Role</th>
        <th>Last Active</th>
        <th>{getOrgTermsContent('projects')}</th>
        <th>{getOrgTermsContent('initiatives')}</th>
        <th>Change Impacts</th>
        <th style={{ textAlign: 'end' }}>Actions</th>
      </tr>
    </thead>
    <tbody>
      {users.map((user) =>
        UsersTableRow(
          slug,
          user,
          currentUserId,
          onDelete,
          onSignOut,
          permissions,
          restrictions,
          currentUserRole
        )
      )}
    </tbody>
  </Table>
);

UsersTable.propTypes = {
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      fullName: PropTypes.string.isRequired,
      changeImpactsCount: PropTypes.number || null,
      initiativesCount: PropTypes.number || null,
      invitedAt: PropTypes.string || null,
      lastActive: PropTypes.string || null,
      orgRole: PropTypes.string || null,
      projectsCount: PropTypes.number || null,
      status: PropTypes.string,
    })
  ).isRequired,
};

export default UsersTable;
